<template>
	<EventLayout :event="event" :subtitle="trans('enrollments.purchase_orders.title')">
		<div class="flex flex-col items-stretch lg:flex-row lg:items-end gap-2">
			<LabeledObject full>
				<template #label>{{ trans('enrollments.purchase_orders.enrollee') }}</template>
				<DropdownSearch id="enrollee-dropdown-search" :items="enrollees" search-attribute="full_name" :default-attribute="selectedEnrollee?.full_name" full @select="selectEnrollee"/>
			</LabeledObject>
			<LabeledObject>
				<template #label>{{ trans('general.status.title') }}</template>
				<select v-model="selectedStatus" @change.stop="reload">
					<option v-for="status in PurchaseOrderStatus" :value="status">{{ trans(`enrollments.purchase_orders.status.${status}`) }}</option>
				</select>
			</LabeledObject>
			<LabeledObject>
				<template #label>{{ trans('general.sort.title') }}</template>
				<select v-model="sortingType" @change.stop="reload">
					<option value="created_at">{{ trans('general.sort.options.created_at') }}</option>
					<option value="uploaded_at">{{ trans('general.sort.options.uploaded_at') }}</option>
				</select>
			</LabeledObject>
			<LabeledObject>
				<template #label>{{ trans('general.sort.order.title') }}</template>
				<select v-model="sortingDirection" @change.stop="reload">
					<option value="asc">{{ trans('general.sort.order.ascending') }}</option>
					<option value="desc">{{ trans('general.sort.order.descending') }}</option>
				</select>
			</LabeledObject>
		</div>
		<Pagination :resource="purchaseOrders" emit-link @redirect="switchPage($event)"/>
		<Empty v-if="purchaseOrders.data.length === 0"/>
		<CardGrid v-else tight>
			<PurchaseOrderComponent
				v-for="purchaseOrder in purchaseOrders.data"
				:key="`purchase-order-${purchaseOrder.id}`"
				:event="event"
				:purchase-order="purchaseOrder"
				:can-update="canUpdate"
				:can-delete="canDelete"
				@delete="remove"
			/>
		</CardGrid>
		<Pagination :resource="purchaseOrders" emit-link @redirect="switchPage($event)"/>
	</EventLayout>
</template>
<script setup lang="ts">
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {router} from '@inertiajs/vue3'
import {type User} from '@/Types/User'
import Empty from '@/Components/UI/Empty.vue'
import {defineProps, type Ref, ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import EventLayout from '@/Layouts/EventLayout.vue'
import Pagination from '@/Components/UI/Pagination.vue'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {PurchaseOrderStatus} from '@/Types/Enums/PurchaseOrderStatus'
import {type PaginatedModel} from '@/Types/Pagination/PaginatedModel'
import DropdownSearch from '@/Components/UI/Dropdown/DropdownSearch.vue'
import {type PurchaseOrder} from '@/Types/Events/Enrollments/PurchaseOrders/PurchaseOrder'
import PurchaseOrderComponent from '@/Components/Events/Enrollments/PurchaseOrders/PurchaseOrder.vue'

const props = defineProps<{
	event:Event,
	canUpdate:boolean,
	canDelete:boolean,
	sortingType:string,
	enrollees:Array<User>,
	sortingDirection:string,
	selectedEnrollee:User|null,
	selectedStatus:PurchaseOrderStatus,
	purchaseOrders:PaginatedModel<PurchaseOrder>,
}>()

const event:Ref<Event> = ref(props.event)
const sortingType:Ref<string> = ref(props.sortingType)
const enrollees:Ref<Array<User>> = ref(props.enrollees)
const sortingDirection:Ref<string> = ref(props.sortingDirection)
const selectedEnrollee:Ref<User|null> = ref(props.selectedEnrollee)
const selectedStatus:Ref<PurchaseOrderStatus> = ref(props.selectedStatus)
const purchaseOrders:Ref<PaginatedModel<PurchaseOrder>> = ref(props.purchaseOrders)

function reload():void{
	router.get(route('events.enrollments.purchase-orders.index', {
		event: event.value.id,
		status: selectedStatus.value,
		sortingType: sortingType.value,
		user_id: selectedEnrollee.value?.id,
		sortingDirection: sortingDirection.value,
	}))
}

function switchPage(url:string):void{
	let link = new URL(url)
	link.searchParams.append('event', event.value.id)
	link.searchParams.append('status', selectedStatus.value)
	link.searchParams.append('sortingType', sortingType.value)
	link.searchParams.append('sortingDirection', sortingDirection.value)
	if(selectedEnrollee.value){
		link.searchParams.append('user_id', selectedEnrollee.value.id)
	}
	router.get(link.href)
}

function selectEnrollee(enrollee:User|null):void{
	selectedEnrollee.value = enrollee
	reload()
}

function remove(purchaseOrderId:number):void{
	const index = purchaseOrders.value.data.findIndex((e:PurchaseOrder) => e.id === purchaseOrderId)
	if(index > -1){
		purchaseOrders.value.data.splice(index, 1)
	}
}
</script>